import type { AllFoldersStatus } from "../api/entitlementsAdminApi";

export interface OnboardingInput {
  asset: { asset_id: number; asset_name: string } | null;
  inAllAssets: boolean;
  folders: AllFoldersStatus | null;
  /** null when the resources call failed. */
  resourceCount: number | null;
  /** null when the Outage-Assets group does not exist. */
  inOutageGroup: boolean | null;
}

export type ChecklistAction =
  | "addToAllAssets"
  | "grantFolders"
  | "openResources"
  | "toggleOutage";

export interface ChecklistStep {
  id: "record" | "visible" | "folders" | "resources" | "outage";
  label: string;
  /** One line under the label: what the step means or what to do about it. */
  help: string;
  status: "done" | "missing" | "warn" | "choice";
  action?: ChecklistAction;
}

export function buildChecklist(input: OnboardingInput): ChecklistStep[] {
  const { asset, inAllAssets, folders, resourceCount, inOutageGroup } = input;

  const outage: ChecklistStep =
    inOutageGroup === null
      ? {
          id: "outage",
          status: "choice",
          label: "Outage reporting: the Outage-Assets group doesn't exist yet",
          help: "Nothing can be set here until that group exists.",
        }
      : {
          id: "outage",
          status: "choice",
          action: "toggleOutage",
          ...(inOutageGroup
            ? {
                label: "Outage reporting: in the Outage-Assets group",
                help: "Leave out upload-only accounts.",
              }
            : {
                label: "Outage reporting: not in the Outage-Assets group",
                help: "Add it only if users report outages for this asset.",
              }),
        };

  return [
    {
      id: "record",
      status: asset ? "done" : "missing",
      label: asset ? "Asset record exists" : "Asset record does not exist",
      help: asset
        ? "Asset record exists."
        : "Create it with New asset (or in Assets > Asset).",
    },
    {
      id: "visible",
      status: inAllAssets ? "done" : "missing",
      label: inAllAssets ? "Visible to users" : "Not visible to users",
      help: inAllAssets
        ? "In the BETM-All-Assets group."
        : "Not in the BETM-All-Assets group, so users can't see it.",
      ...(inAllAssets ? {} : { action: "addToAllAssets" as const }),
    },
    {
      id: "folders",
      status: folders?.complete ? "done" : "missing",
      label: folders?.complete
        ? "FileCenter all-folders permissions granted"
        : "FileCenter all-folders permissions missing",
      help: folders?.complete
        ? "The Logic App can write to this asset's FileCenter folders."
        : "The Logic App cannot write to this asset's FileCenter folders.",
      ...(folders?.complete ? {} : { action: "grantFolders" as const }),
    },
    resourceCount === null
      ? {
          id: "resources",
          status: "warn",
          label: "Can't read resources",
          help: "Your access may be stale. An admin can click Refresh entitlements on the Entitlements tab (up to 15 minutes), or wait for the hourly refresh at :06.",
        }
      : {
          id: "resources",
          status: resourceCount > 0 ? "done" : "warn",
          label:
            resourceCount > 0
              ? `${resourceCount} resource${resourceCount === 1 ? "" : "s"} defined`
              : "No resources defined yet",
          help:
            resourceCount > 0
              ? "Manage them on the Resource tab."
              : "Add them on the Resource tab.",
          action: "openResources",
        },
    outage,
  ];
}

/**
 * Resource count from a `GET /resources/{asset_id}` body. The route is paged
 * (default page_size 10), so `meta.total_rows` is the real count.
 */
export function resourceCountFrom(body: any): number {
  const total = body?.meta?.total_rows;
  if (typeof total === "number") return total;
  const list = body?.response?.data;
  return Array.isArray(list) ? list.length : 0;
}

/**
 * Resource count from the settled `GET /resources/{asset_id}` call. The service
 * answers 404 when the asset has no resources yet, so a 404 is 0. Any other
 * failure (401, 403, 5xx, no response) means the list couldn't be read: null.
 */
export function resourceCountFromSettled(
  result: PromiseSettledResult<{ data?: unknown }>,
): number | null {
  if (result.status === "fulfilled") return resourceCountFrom(result.value.data);
  return (result.reason as { response?: { status?: number } } | null)?.response?.status === 404 ? 0 : null;
}
